import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpRequest, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http'
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { finalize, filter } from 'rxjs/operators';


// Модель опций, которые прокидываются в request ангуляровского http клиента
type MimeHttpOptions = {
  headers?: HttpHeaders;
  reportProgress?: boolean;
  params?: HttpParams;
  responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
  withCredentials?: boolean;
};

export enum Action { QueryStart, QueryStop }

@Injectable({
  providedIn: 'root'
})
export class AuthHttpService {

  process: EventEmitter<any> = new EventEmitter<any>();
  authFailed: EventEmitter<any> = new EventEmitter<any>();

  constructor(private _http: HttpClient, private _router: Router) { }

  private static _buildAuthHeader(): string {
    //return localStorage.getItem("authToken");
    return "Bearer " + localStorage.getItem("token");
  }

  public get(url: string, options?: MimeHttpOptions): Observable<HttpResponse<any>> {
    return this._request('GET', url, null, options);
  }

  public post(url: string, body: any, options?: MimeHttpOptions): Observable<HttpResponse<any>> {
    return this._request('POST', url, body, options);
  }

  public put(url: string, body: any, options?: MimeHttpOptions): Observable<HttpResponse<any>> {
    return this._request('PUT', url, body, options);
  }

  public delete(url: string, options?: MimeHttpOptions): Observable<HttpResponse<any>> {
    return this._request('DELETE', url, null, options);
  }

  public patch(url: string, body: any, options?: MimeHttpOptions): Observable<HttpResponse<any>> {
    return this._request('PATCH', url, body, options);
  }

  public head(url: string, options?: MimeHttpOptions): Observable<HttpResponse<any>> {
    return this._request('HEAD', url, null, options);
  }

  private _request(method: string, url: string, body?: any, options?: MimeHttpOptions): Observable<HttpResponse<any>> {

    if (typeof body !== 'string') {
        const formData =  new FormData();
      for ( var key in body ) {
        formData.append(key, body[key]);
      }
      body = formData;
    }

    if (!options) options = {};

    if (!options.headers) {
      options.headers = new HttpHeaders();
    }

    options.headers = options.headers.set("Authorization", AuthHttpService._buildAuthHeader());

    return new Observable((observer) => {
      this.process.next(Action.QueryStart);
      this._http.request(new HttpRequest(method, environment.BaseUrlAPI + url, body, options)).pipe(
          filter(event => {
            return event instanceof HttpResponse;
          }),
          finalize(() => {
            this.process.next(Action.QueryStop);
          }),)
          .subscribe(
              (res) => {
                observer.next(<HttpResponse<any>>res);
                observer.complete();
              },
              (err) => {
                switch (err.status) {
                  case 401:
                    this._router.navigate(['/login']);
                    this.authFailed.next(err);
                    observer.error(err);
                    break;
                  case 403:
                    this._router.navigate(['/auth/account-expired']);
                    this.authFailed.next(err);
                    observer.error(err);
                    break;
                  case 423:
                    this._router.navigate(['/auth/account-disabled']);
                    this.authFailed.next(err);
                    observer.error(err);
                    break;
                  default:
                    observer.error(err);
                    break;
                }
              })
    })
  };

  /**
   * Сделает авторизованный GET запрос
   * @param url - урл по которому сделать запрос
   * @param responseType -тип данных, которые ожидаем в ответ: json или text
   * @param errHandler - ф-ция, которая сработает при обработке ошибок, может быть опущена
   * @returns returnPromise:Promise<any> - вернет выбранный готовый промиз (fulfilled), или неготовый(rejected), выпонив ф-цию обработки ошибок
   */
  public fetchGet(url:string, responseType: 'json' | 'text', errHandler = null): Promise<any> {
    url = environment.BaseUrlAPI + url;
    let returnPromise = fetch(url, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        'Authorization': AuthHttpService._buildAuthHeader(),
      }
    }).then(response => {
      if (responseType == 'json') {
        return response.json();
      } else {
        return response.text();
      }
    });
    if (errHandler != null) returnPromise.catch(err => errHandler(err=null));
    return returnPromise
  }

  /**
   * сделает обработанный POST запрос
   * @param url - урл по которому сделать запрос
   * @param data - данные, которые посылаем: либо JSON не упакованный в строку, либо объект FormData
   * @param requestType - тип данных, которые посылаем: json или formdata
   * @param responseType - тип данных, которые ожидаем в ответ: json или text
   * @param errHandler - ф-ция обрабтного вызова, коотрая сработает при ошибке, может быть опущена
   * @returns returnPromise:Promise<any> - вернет выбранный готовый промиз (fulfilled), или неготовый(rejected), выпонив ф-цию обработки ошибок
   */
  public fetchPost(url:string, data: any | FormData, requestType: 'json' | 'formData', responseType: 'json' | 'text', errHandler = null): Promise<any> {
    url = environment.BaseUrlAPI + url;
    let options = {};
    options['method'] = 'POST';
    options['mode'] = 'cors';
    options['cache'] = 'no-cache';
    if (requestType == 'json') {
      options['headers'] = {
        'Authorization': AuthHttpService._buildAuthHeader(),
        'Content-Type': 'application/json'
      };
      options['body'] = JSON.stringify(data)
    } else {
      options['headers'] = {
        'Authorization': AuthHttpService._buildAuthHeader(),
      };
      options['body'] = data
    }
    let returnPromise = fetch(url, options).then(response => {
      if (responseType == 'json') {
        return response.json();
      } else {
        return response.text();
      }
    });
    if (errHandler != null) returnPromise.catch(err => errHandler(err));
    return returnPromise
  }
}

// function helpers
/**
 * Функция получает объект и возвращает экземпляр FormData
 * @param formValue - объект
 */
export function toFormData<T>(formValue: T) {
  const formData = new FormData();

  for (const key of Object.keys(formValue)) {
    const value = formValue[key];
    formData.append(key, value);
  }

  return formData;
}
